import { DatePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  ViewChild,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SubscriptionId, UserSubscriptionHistoryItemDto } from '@workspace/data-types';
import { PriceDisplayPipe, TransactionStatusDisplayPipe } from '@workspace/ng-data-access';
import { SubscriptionStatusChipComponent } from '../../chips/subscription-status-chip/subscription-status-chip.component';

@Component({
  selector: 'aura-user-subscription-history',
  imports: [
    DatePipe,
    MatButtonModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    PriceDisplayPipe,
    TransactionStatusDisplayPipe,
    SubscriptionStatusChipComponent,
  ],
  templateUrl: './subscription-history.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSubscriptionHistoryAuraComponent implements AfterViewInit {
  private destroyRef = inject(DestroyRef);

  history = input<UserSubscriptionHistoryItemDto[]>([]);
  loading = input<boolean>(false);
  loadingMore = input<boolean>(false);
  hasMore = input<boolean>(false);
  total = input<number>(0);
  defaultOpen = input<boolean>(false);

  loadMore = output<void>();

  isOpen = signal<boolean>(false);

  @ViewChild('sentinel') sentinelRef?: ElementRef<HTMLDivElement>;

  // Set of subscription IDs whose transactions section is expanded
  expandedSubscriptions = signal<Record<string, boolean>>({});

  constructor() {
    // If defaultOpen is true on init
    if (this.defaultOpen()) {
      this.isOpen.set(true);
    }
  }

  toggleOpen(): void {
    const next = !this.isOpen();
    this.isOpen.set(next);
    if (next && this.history().length === 0 && !this.loading()) {
      this.loadMore.emit();
    }
  }

  ngAfterViewInit(): void {
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          entry?.isIntersecting &&
          this.isOpen() &&
          this.hasMore() &&
          !this.loading() &&
          !this.loadingMore()
        ) {
          this.loadMore.emit();
        }
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0.1,
      },
    );

    if (this.sentinelRef?.nativeElement) {
      observer.observe(this.sentinelRef.nativeElement);
    }

    this.destroyRef.onDestroy(() => {
      observer.disconnect();
    });
  }

  toggleExpanded(subId: SubscriptionId | string): void {
    const key = String(subId);
    this.expandedSubscriptions.update((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }

  isExpanded(subId: SubscriptionId | string): boolean {
    const key = String(subId);
    const state = this.expandedSubscriptions()[key];
    // By default, expanded if not explicitly toggled to false
    return state === undefined ? true : state;
  }

  getTransactionStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'succeeded':
        return 'bg-jade-3 text-jade-11 border-jade-5';
      case 'failed':
        return 'bg-crimson-3 text-crimson-11 border-crimson-5';
      case 'pending':
      case 'requires_action':
        return 'bg-amber-3 text-amber-11 border-amber-5';
      default:
        return 'bg-neutral-a3 text-neutral-11 border-neutral-a5';
    }
  }

  getTransactionStatusIcon(status: string): string {
    switch (status?.toLowerCase()) {
      case 'succeeded':
        return 'check_circle';
      case 'failed':
        return 'cancel';
      case 'pending':
      case 'requires_action':
        return 'hourglass_top';
      default:
        return 'help_outline';
    }
  }
}
